import type { Ref } from 'react';

import Carousel, {
	type CarouselRef,
	type Orientation,
} from 'components/Carousel';
import { IconCircle } from 'components/Icon';
import Img from 'components/Img';
import PlainButton from 'components/PlainButton';
import { useRefObject, useValueChangeEffect } from 'hooks';
import { cn } from 'utils/classNames';
import { is } from 'utils/helpers';
import { useI18n } from 'utils/i18n';

type Size = 'md' | 'sm';
export interface Image {
	height?: number;
	isVideo?: boolean;
	src: string;
	width?: number;
}

interface Props {
	carouselClassName?: string;
	className?: string;
	hasControls?: boolean;
	/**
	 * Use width and height specified on each image, if available. Off by default
	 * since passing AssetResponse objects could result in full size images.
	 */
	hasCustomImageDimensions?: boolean;
	images: Image[];
	isScreenReaderHidden?: boolean;
	onThumbnailClick: (index: number) => void;
	orientation?: Orientation;
	ref?: Ref<CarouselRef>;
	selectedImageIndex?: number;
	size: Size;
}

const SIZES: Record<Size, { length: number; sizeClass: string }> = {
	sm: { length: 48, sizeClass: 'size-12' },
	md: { length: 80, sizeClass: 'size-12 sm:size-20' },
};

export default function ThumbnailList({
	carouselClassName,
	className,
	hasControls = false,
	hasCustomImageDimensions = false,
	images,
	isScreenReaderHidden = false,
	onThumbnailClick,
	orientation = 'horizontal',
	ref,
	selectedImageIndex,
	size,
}: Props) {
	const { t } = useI18n();
	const carouselRef = useRefObject(ref);
	useValueChangeEffect(selectedImageIndex, () => {
		if (is.number(selectedImageIndex)) {
			carouselRef.current?.goToIndex(selectedImageIndex, { animate: false });
		}
	});

	if (images.length < 2) {
		return null;
	}

	const { length, sizeClass } = SIZES[size];

	// TODO: Create a ButtonCarousel that handles some of the index stuff?
	return (
		<Carousel
			ref={carouselRef}
			controlsLayout="inline"
			controlsSize="xs"
			orientation={orientation}
			hasControls={hasControls}
			// Let the controls decide the alignment, if they're active.
			alignment={hasControls ? undefined : 'center'}
			hasProgressTracker={false}
			animationDuration="fast"
			initialIndex={selectedImageIndex}
			aria-hidden={isScreenReaderHidden}
			className={cn('w-fit max-w-full', className)}
			carouselClassName={cn(
				'flex gap-2',
				orientation === 'vertical' && 'flex-col',
				carouselClassName,
			)}
			buttonsClassName={cn(
				size === 'md' && [
					orientation === 'horizontal' && 'after:-inset-y-8',
					orientation === 'vertical' && 'after:-inset-x-8',
				],
			)}
			items={images.map((image, i) => (
				<PlainButton
					key={image.src}
					aria-label={t('product_details_show_image_number_label', {
						num: i + 1,
					})}
					tabIndex={isScreenReaderHidden ? -1 : undefined}
					className={cn(
						'block rounded-xs border hover:border-strong',
						image.isVideo && 'relative',
						sizeClass,
						size === 'sm' && 'p-0.5',
						size === 'md' && 'p-1',
						i === selectedImageIndex &&
							'border-focus outline-1 outline-focus outline-solid',
						i !== selectedImageIndex &&
							'border-transparent hover:forced-colors-outline',
					)}
					onClick={() => {
						if (i !== selectedImageIndex) {
							onThumbnailClick(i);
						}
					}}
				>
					<Img
						src={image.src}
						alt=""
						width={
							hasCustomImageDimensions && image.width ? image.width : length
						}
						height={
							hasCustomImageDimensions && image.height ? image.height : length
						}
						className={cn(
							'overflow-hidden',
							size === 'sm' && 'rounded-2xs',
							size === 'md' && 'rounded-2xs sm:rounded-xs',
						)}
					/>
					{image.isVideo && (
						<IconCircle
							name="play"
							variant="transparent"
							padding="sm"
							className="absolute top-1/2 left-1/2 z-1 -translate-1/2"
						/>
					)}
				</PlainButton>
			))}
		/>
	);
}
ThumbnailList.displayName = 'ThumbnailList';
